import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getApprLimitWorkflows,
    getAllApprovalLimits,
    saveApprovalLimit,
} from '../../api/AccountsAPI/approvalLimitSettingsAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

/** Master Operations still eligible for a new approval limit */
export const fetchApprLimitWorkflows = createAsyncThunk(
    'approvalLimitSettings/fetchApprLimitWorkflows',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getApprLimitWorkflows();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the workflow list');
        }
    }
);

/** Grid of all configured approval limits */
export const fetchApprovalLimitsGrid = createAsyncThunk(
    'approvalLimitSettings/fetchApprovalLimitsGrid',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getAllApprovalLimits();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load approval limits');
        }
    }
);

/** Add / Update / Delete an approval limit */
export const submitApprovalLimit = createAsyncThunk(
    'approvalLimitSettings/submitApprovalLimit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await saveApprovalLimit(payload);
            const status = res.data?.Data;
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to save the approval limit');
            }
            return { action: payload.Action };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save the approval limit');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    workflowList: [],
    gridList: [],
    saveResult: null,

    loading: {
        workflows: false,
        grid: false,
        save: false,
    },
    errors: {
        workflows: null,
        grid: null,
        save: null,
    },
};

const approvalLimitSettingsSlice = createSlice({
    name: 'approvalLimitSettings',
    initialState,
    reducers: {
        clearApprovalLimitSaveResult: (state) => { state.saveResult = null; state.errors.save = null; },
        resetApprovalLimitSettings: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchApprLimitWorkflows.pending, s => { s.loading.workflows = true; s.errors.workflows = null; })
            .addCase(fetchApprLimitWorkflows.fulfilled, (s, a) => { s.loading.workflows = false; s.workflowList = a.payload; })
            .addCase(fetchApprLimitWorkflows.rejected, (s, a) => { s.loading.workflows = false; s.errors.workflows = a.payload; });

        builder
            .addCase(fetchApprovalLimitsGrid.pending, s => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchApprovalLimitsGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridList = a.payload; })
            .addCase(fetchApprovalLimitsGrid.rejected, (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(submitApprovalLimit.pending, s => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitApprovalLimit.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitApprovalLimit.rejected, (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearApprovalLimitSaveResult,
    resetApprovalLimitSettings,
} = approvalLimitSettingsSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectWorkflowList = s => s.approvalLimitSettings.workflowList;
export const selectApprovalLimitsGrid = s => s.approvalLimitSettings.gridList;
export const selectApprovalLimitSaveResult = s => s.approvalLimitSettings.saveResult;

export const selectWorkflowsLoading = s => s.approvalLimitSettings.loading.workflows;
export const selectApprovalGridLoading = s => s.approvalLimitSettings.loading.grid;
export const selectApprovalSaveLoading = s => s.approvalLimitSettings.loading.save;
export const selectApprovalGridError = s => s.approvalLimitSettings.errors.grid;
export const selectApprovalSaveError = s => s.approvalLimitSettings.errors.save;

export default approvalLimitSettingsSlice.reducer;
